/**
 * What the Promote flow shows before anything is sent: the settings it starts from, what they amount to
 * (which writes actually run without asking), and the warnings the backend will record. Mirrors the
 * backend's rules so the review matches what Promote applies; the backend enforces them again.
 */

import type { PromotionPlan, PromotionType, PromotionWarning, ReplyMode, ScenarioSetting, WriteMode } from './types';

/** The settings in force: the active promotion's (the plan already reads them), else safe ones. */
export const initialSettings = (plan: PromotionPlan): Record<string, ScenarioSetting> =>
	Object.fromEntries(
		plan.types.map((type) => [
			type.typeId,
			{
				reply: type.reply.lockedReason ? 'draft' : type.reply.mode,
				writes: Object.fromEntries(type.writes.map((write) => [write.tool, write.lockedReason ? 'ask' : write.mode]))
			}
		])
	);

/** The same reply and write modes for every type; what the backend refuses to promote again. */
export const settingsEqual = (a: Record<string, ScenarioSetting>, b: Record<string, ScenarioSetting>): boolean => {
	const typeIds = Object.keys(a).sort();
	if (typeIds.join('\n') !== Object.keys(b).sort().join('\n')) return false;
	return typeIds.every((typeId) => {
		const left = a[typeId]!;
		const right = b[typeId]!;
		const tools = Object.keys(left.writes).sort();
		return (
			left.reply === right.reply &&
			tools.join('\n') === Object.keys(right.writes).sort().join('\n') &&
			tools.every((tool) => left.writes[tool] === right.writes[tool])
		);
	});
};

/** True when the protocol is promoted and these settings are the ones in force: Promote would change nothing. */
/** The drafted templates Promote adds: the latest draft of each type back testing has cleared. */
export const draftIdsToAdd = (plan: PromotionPlan): string[] =>
	plan.types.flatMap((type) => (type.reply.draft?.addedOnPromote ? [type.reply.draft.draftId] : []));

/** Promoting again changes nothing: same settings in force and no drafted template waiting to be added. */
export const isUnchangedPromotion = (plan: PromotionPlan, settings: Record<string, ScenarioSetting>): boolean =>
	plan.promotion?.status === 'active' &&
	!plan.promotion.outOfDate &&
	draftIdsToAdd(plan).length === 0 &&
	settingsEqual(settings, initialSettings(plan));

export const withReply = (
	settings: Record<string, ScenarioSetting>,
	typeId: string,
	reply: ReplyMode
): Record<string, ScenarioSetting> => ({
	...settings,
	[typeId]: { ...settings[typeId]!, reply }
});

/**
 * A scenario Promote can change: its reply or one of its writes is not locked. Every other scenario stays a
 * draft that asks first (the starting settings already hold locked switches there), so the Scenarios step
 * leads with these and folds the rest into one line each.
 */
export const isPromotable = (type: PromotionType): boolean =>
	type.reply.lockedReason === null || type.writes.some((write) => write.lockedReason === null);

/** Every reply that can auto-send set to `reply`; Draft also reaches a reply that cannot. */
export const withAllReplies = (
	types: PromotionType[],
	settings: Record<string, ScenarioSetting>,
	reply: ReplyMode
): Record<string, ScenarioSetting> =>
	types.reduce(
		(next, type) => (reply === 'draft' || type.reply.lockedReason === null ? withReply(next, type.typeId, reply) : next),
		settings
	);

export const withWrite = (
	settings: Record<string, ScenarioSetting>,
	typeId: string,
	tool: string,
	mode: WriteMode
): Record<string, ScenarioSetting> => ({
	...settings,
	[typeId]: {
		...settings[typeId]!,
		writes: { ...settings[typeId]!.writes, [tool]: mode }
	}
});

/** A scenario runs `tool` without asking: set to run it, with a reply that auto-sends. Siblings do not matter. */
const runsWithoutAsking = (setting: ScenarioSetting | undefined, tool: string): boolean =>
	setting?.reply === 'auto' && setting.writes[tool] === 'auto';

/** Tools that run without asking for at least one scenario (for that scenario's reply only). */
export const autoWritesOf = (types: PromotionType[], settings: Record<string, ScenarioSetting>): Set<string> =>
	new Set(
		types.flatMap((type) =>
			type.writes.map((write) => write.tool).filter((tool) => runsWithoutAsking(settings[type.typeId], tool))
		)
	);

/** A scenario set to run a write without asking while its reply drafts; the backend refuses to promote it. */
export interface PromotionBlocker {
	typeId: string;
	message: string;
}

export const blockersOf = (types: PromotionType[], settings: Record<string, ScenarioSetting>): PromotionBlocker[] =>
	types.flatMap((type): PromotionBlocker[] => {
		const setting = settings[type.typeId];
		if (!setting || setting.reply === 'auto') return [];
		const running = type.writes.map((write) => write.tool).filter((tool) => setting.writes[tool] === 'auto');
		if (!running.length) return [];
		const tools = running.join(', ');
		return [
			{
				typeId: type.typeId,
				message: `${tools} cannot run without asking while the reply is a draft: the customer would hear nothing after it. Auto-send the reply or set ${tools} to ask first.`
			}
		];
	});

export const warningsOf = (types: PromotionType[], settings: Record<string, ScenarioSetting>): PromotionWarning[] =>
	types.flatMap((type): PromotionWarning[] => {
		const setting = settings[type.typeId];
		if (!setting || setting.reply !== 'auto') return [];
		const asking = type.writes.map((write) => write.tool).filter((tool) => !runsWithoutAsking(setting, tool));
		if (!asking.length) return [];
		return [
			{
				typeId: type.typeId,
				kind: 'reply_waits_for_approval',
				message: `The reply waits for approval of ${asking.join(', ')}.`
			}
		];
	});

export interface PromotionSummary {
	autoReplies: PromotionType[];
	draftReplies: PromotionType[];
	autoWrites: string[];
}

export const summaryOf = (types: PromotionType[], settings: Record<string, ScenarioSetting>): PromotionSummary => ({
	autoReplies: types.filter((type) => settings[type.typeId]?.reply === 'auto'),
	draftReplies: types.filter((type) => settings[type.typeId]?.reply !== 'auto'),
	autoWrites: [...autoWritesOf(types, settings)].sort()
});
